import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Lock, Mail, Shield, Sparkles, ArrowRight, Eye, EyeOff } from "lucide-react";
import { motion } from "framer-motion";
import { GESIL_LOGO_URL } from "../../data/content";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Admin.");
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen relative overflow-hidden bg-azure-mesh flex">
      {/* Animated background orbs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-[#2563EB] opacity-[0.12] blur-3xl animate-pulse" />
        <div className="absolute top-1/3 -right-32 w-[420px] h-[420px] rounded-full bg-[#60A5FA] opacity-[0.18] blur-3xl" />
        <div className="absolute -bottom-32 left-1/3 w-[360px] h-[360px] rounded-full bg-[#93C5FD] opacity-[0.20] blur-3xl" />
        <div className="absolute inset-0 bg-grid-blue opacity-40" />
      </div>

      {/* Left brand panel */}
      <div className="hidden lg:flex flex-1 relative p-16 text-[#0F172A]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative max-w-md self-center"
        >
          <Link to="/" className="inline-flex items-center group">
            <img src={GESIL_LOGO_URL} alt="GESIL" className="h-20 w-auto object-contain" />
          </Link>

          <div className="mt-14 inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur border border-blue-200 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#2563EB]">
            <Sparkles size={12} /> Admin Console
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold mt-6 leading-[1.05] tracking-tight">
            Manage the <span className="text-[#2563EB]">GESIL</span> experience.
          </h1>
          <p className="mt-6 text-[#475569] text-base leading-relaxed">
            Update hero slides, projects, leadership profiles, blogs and careers. Track inbound
            consultations and demo requests — all from one secure place.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-4">
            {[
              { label: "Hero Slides", value: "6" },
              { label: "Live Projects", value: "10+" },
              { label: "Directors", value: "5" },
              { label: "Audit Trail", value: "100%" },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-xl bg-white/60 backdrop-blur border border-blue-100 p-4"
              >
                <div className="font-display text-2xl text-[#0F172A] font-semibold">{s.value}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-[#475569] mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-10 relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="lg:hidden mb-6 flex justify-center">
            <Link to="/">
              <img src={GESIL_LOGO_URL} alt="GESIL" className="h-14 w-auto object-contain" />
            </Link>
          </div>

          <form
            onSubmit={submit}
            data-testid="admin-login-form"
            className="relative w-full bg-white/85 backdrop-blur-2xl rounded-3xl p-8 md:p-10 shadow-[0_40px_120px_-40px_rgba(37,99,235,0.45)] border border-white/80 ring-1 ring-blue-100"
          >
            {/* Top icon badge */}
            <div className="absolute -top-6 left-8 inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] text-white shadow-[0_18px_40px_-12px_rgba(37,99,235,0.7)]">
              <Shield size={20} />
            </div>

            <div className="pt-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2563EB]">
                Sign in
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold mt-2 text-[#0F172A] leading-tight">
                Welcome back.
              </h2>
              <p className="mt-2 text-[#475569] text-sm">
                Use your GESIL admin credentials to enter the CMS.
              </p>
            </div>

            {/* Fields */}
            <div className="mt-8 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#0F172A]">
                  Email
                </label>
                <div className="mt-1.5 relative">
                  <Mail
                    size={16}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-400"
                  />
                  <input
                    type="email"
                    data-testid="admin-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-blue-200 bg-white focus:border-[#2563EB] focus:ring-4 focus:ring-blue-100 outline-none text-[#0F172A] text-sm transition-all"
                    placeholder="admin@gesil.co.in"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#0F172A]">
                  Password
                </label>
                <div className="mt-1.5 relative">
                  <Lock
                    size={16}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-400"
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    data-testid="admin-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-11 pr-12 py-3 rounded-xl border border-blue-200 bg-white focus:border-[#2563EB] focus:ring-4 focus:ring-blue-100 outline-none text-[#0F172A] text-sm transition-all"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    data-testid="toggle-password-visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#2563EB] p-1.5"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] hover:from-[#1D4ED8] hover:to-[#1E40AF] disabled:opacity-60 disabled:cursor-not-allowed text-white py-3.5 font-semibold uppercase tracking-wider text-sm transition-all shadow-[0_20px_45px_-18px_rgba(37,99,235,0.75)]"
            >
              {loading ? (
                <>
                  <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign in to CMS <ArrowRight size={16} />
                </>
              )}
            </button>

            <div className="mt-6 flex items-center justify-between text-xs">
              <Link
                to="/"
                data-testid="back-to-website"
                className="text-[#475569] hover:text-[#2563EB] inline-flex items-center gap-1"
              >
                ← Back to website
              </Link>
              <span className="text-[#94A3B8]">Secure · Audited · v1.0</span>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
